import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, Square, Loader2, Wand2, BookOpen, ExternalLink, MessageSquareWarning, Sparkles, Info, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const RecoAssistant = ({ onTrain }) => {
  const [situation, setSituation] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [result, setResult] = useState(null);
  const [openFiche, setOpenFiche] = useState(null);
  const [modules, setModules] = useState([]);
  const [cases, setCases] = useState([]);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    api.modules().then(setModules).catch(() => {});
    api.productCases().then((d) => setCases(d.cases || [])).catch(() => {});
  }, []);

  const modBySlug = useMemo(() => Object.fromEntries(modules.map((m) => [m.slug, m])), [modules]);
  const caseById = useMemo(() => Object.fromEntries(cases.map((c) => [c.id, c])), [cases]);

  const analyze = async () => {
    if (!situation.trim()) return toast.error("Décrivez d'abord la situation du client.");
    setLoading(true);
    setResult(null);
    setOpenFiche(null);
    try {
      setResult(await api.recommend(situation));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recommandation indisponible.");
    } finally {
      setLoading(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 800) return toast.error("Aucune parole détectée.");
        setLoading(true);
        try {
          const text = await api.transcribeAudio(blob);
          setSituation((s) => (s ? s + " " : "") + text);
        } catch {
          toast.error("Transcription impossible.");
        } finally {
          setLoading(false);
        }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Micro inaccessible. Autorisez le micro ou tapez le texte.");
    }
  };
  const stopRec = () => { recRef.current?.stop(); setRecording(false); };

  return (
    <div data-testid="reco-assistant">
      <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
        <Sparkles size={16} className="text-[#C89A3E]" />
        <span>Décrivez une vraie situation client — l'IA suggère les produits, les objections à prévoir et les modules/cas AMF liés.</span>
      </div>
      <textarea data-testid="reco-input" value={situation} onChange={(e) => setSituation(e.target.value)} rows={4}
        placeholder="Ex. : Couple, 40 ans, deux enfants, hypothèque de 300 000 $, un seul revenu de 75 000 $, peu d'épargne…"
        className="w-full p-4 rounded-2xl border border-[#012141]/15 bg-white text-sm focus:outline-none focus:border-[#C89A3E]" />
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {!recording ? (
          <button data-testid="reco-mic-btn" onClick={startRec} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#012141]/20 text-[#012141] font-semibold text-sm hover:border-[#C89A3E] disabled:opacity-50">
            <Mic size={16} /> Dicter
          </button>
        ) : (
          <button data-testid="reco-stop-btn" onClick={stopRec}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-red-600 text-white font-semibold text-sm animate-pulse">
            <Square size={14} /> Arrêter la dictée
          </button>
        )}
        <button data-testid="reco-analyze-btn" onClick={analyze} disabled={loading || recording}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#012141] text-white font-semibold text-sm hover:bg-[#00172E] disabled:opacity-50">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} className="text-[#C89A3E]" />} Obtenir des recommandations
        </button>
      </div>

      {result && (
        <div data-testid="reco-result" className="mt-5 space-y-4">
          {result.synthese && <p className="text-sm text-[#012141] bg-[#FDFDFD] border border-[#012141]/10 rounded-2xl p-3">{result.synthese}</p>}
          {result.produits?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-[#C89A3E] mb-2">Produits à recommander</div>
              <div className="space-y-2">
                {result.produits.map((p, i) => (
                  <div key={i} data-testid={`reco-produit-${i}`} className="rounded-2xl border border-[#012141]/12 bg-white p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-[#012141]">{p.nom}</div>
                        <div className="text-sm text-slate-600 mt-0.5">{p.pourquoi}</div>
                      </div>
                      {p.fiche && (
                        <button data-testid={`reco-fiche-btn-${i}`} onClick={() => setOpenFiche(openFiche === i ? null : i)}
                          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#C89A3E] border border-[#C89A3E]/40 rounded-full px-2.5 py-1 hover:bg-[#C89A3E]/12 transition-colors">
                          <Info size={12} /> Fiche {openFiche === i ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                      )}
                    </div>
                    {p.fiche && openFiche === i && (
                      <div data-testid={`reco-fiche-${i}`} className="mt-3 rounded-xl bg-[#012141]/[0.03] border border-[#012141]/10 p-3 space-y-2.5">
                        {p.fiche.famille && (
                          <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#012141]/8 text-[#012141]">{p.fiche.famille}</span>
                        )}
                        {p.fiche.caracteristiques && (
                          <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#C89A3E]">Caractéristiques clés</div><p className="text-sm text-[#012141] leading-relaxed mt-0.5">{p.fiche.caracteristiques}</p></div>
                        )}
                        {p.fiche.pour_qui && (
                          <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#C89A3E]">À qui ça convient</div><p className="text-sm text-[#012141] leading-relaxed mt-0.5">{p.fiche.pour_qui}</p></div>
                        )}
                        {p.fiche.avantages && (
                          <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#C89A3E]">Atouts</div><p className="text-sm text-[#012141] leading-relaxed mt-0.5">{p.fiche.avantages}</p></div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.avenants?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-[#C89A3E] mb-2">Avenants & garanties iA à proposer</div>
              <div className="space-y-2">
                {result.avenants.map((a, i) => (
                  <div key={i} data-testid={`reco-avenant-${i}`} className="rounded-2xl border border-[#C89A3E]/25 bg-[#C89A3E]/5 p-3">
                    <div className="font-heading font-bold text-[#012141] text-sm">{a.nom}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{a.pourquoi}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.objections?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-orange-700 mb-2 flex items-center gap-1"><MessageSquareWarning size={13} /> Objections à anticiper & meilleures réponses</div>
              <div className="space-y-2">
                {result.objections.map((o, i) => {
                  const objection = typeof o === "string" ? o : o.objection;
                  const reponse = typeof o === "string" ? "" : o.reponse;
                  return (
                    <div key={i} data-testid={`reco-objection-${i}`} className="rounded-2xl border border-orange-200 bg-orange-50/60 p-3">
                      <div className="text-sm text-[#012141] font-semibold flex gap-2"><span className="text-orange-500">“</span>{objection}<span className="text-orange-500">”</span></div>
                      {reponse && (
                        <div className="mt-2 flex gap-2 rounded-xl bg-white border border-[#C89A3E]/30 p-2.5">
                          <Sparkles size={14} className="text-[#C89A3E] mt-0.5 shrink-0" />
                          <div><span className="text-[11px] font-bold uppercase tracking-wide text-[#8A5A1E]">Meilleure réponse</span><p className="text-sm text-[#012141] leading-relaxed mt-0.5">{reponse}</p></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {result.modules?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-[#012141] mb-2">Modules de formation liés</div>
              <div className="flex flex-wrap gap-1.5">
                {result.modules.map((sl) => modBySlug[sl] && (
                  <button key={sl} data-testid={`reco-module-${sl}`} onClick={() => window.open(`/formations/${sl}`, "_blank", "noopener")}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#012141] bg-[#012141]/6 hover:bg-[#C89A3E]/20 px-2 py-1 rounded-full">
                    <BookOpen size={11} /> {modBySlug[sl].title} <ExternalLink size={10} />
                  </button>
                ))}
              </div>
            </div>
          )}
          {result.cas_proches?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-[#012141] mb-2">Cas AMF les plus proches</div>
              <div className="space-y-1.5">
                {result.cas_proches.map((id) => caseById[id] && (
                  <div key={id} className="flex items-center justify-between gap-2 rounded-xl border border-[#012141]/10 bg-white px-3 py-2">
                    <span className="text-sm text-[#012141]">{caseById[id].titre}</span>
                    <button data-testid={`reco-train-${id}`} onClick={() => onTrain?.(caseById[id])}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-[#C89A3E]"><Mic size={12} /> S'entraîner</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.conformite && (
            <p data-testid="reco-compliance" className="text-[11px] leading-relaxed text-slate-500 border-t border-[#012141]/10 pt-3 mt-1">
              {result.conformite}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
